import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { BACKEND_ENABLED } from '../config';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async (token) => {
    try {
      const res = await fetch(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setUser(await res.json());
      } else {
        localStorage.removeItem('smartcart-token');
        setUser(null);
      }
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!BACKEND_ENABLED) { setLoading(false); return; }

    // Google OAuth drops a token in ?google_token=...
    const params = new URLSearchParams(window.location.search);
    const googleToken = params.get('google_token');
    if (googleToken) {
      localStorage.setItem('smartcart-token', googleToken);
      window.history.replaceState({}, '', window.location.pathname);
      fetchMe(googleToken);
      return;
    }

    const token = localStorage.getItem('smartcart-token');
    if (token) {
      fetchMe(token);
    } else {
      setLoading(false);
    }
  }, [fetchMe]);

  function getToken() {
    return localStorage.getItem('smartcart-token');
  }

  async function login(email, password) {
    const res = await fetch(`${API}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Login failed.');
    localStorage.setItem('smartcart-token', data.token);
    setUser(data.user);
    return data;
  }

  async function register(email, password, name) {
    const res = await fetch(`${API}/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, name }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Registration failed.');
    localStorage.setItem('smartcart-token', data.token);
    setUser(data.user);
    return data;
  }

  async function updateProfile(updates) {
    const res = await fetch(`${API}/auth/profile`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getToken()}`,
      },
      body: JSON.stringify(updates),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Update failed.');
    setUser(data);
    return data;
  }

  async function toggleFavourite(name, store) {
    const token = getToken();
    if (!token) return;
    const res  = await fetch(`${API}/auth/favourites/toggle`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ name, store }),
    });
    const data = await res.json();
    if (res.ok) setUser(u => ({ ...u, favourites: data.favourites }));
  }

  async function refreshUser() {
    const token = getToken();
    if (!token) return;
    try {
      const res = await fetch(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setUser(await res.json());
    } catch {}
  }

  async function recordBehaviour(item) {
    const token = getToken();
    if (!token || !item) return;
    try {
      await fetch(`${API}/auth/behaviour/add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          name: item.name,
          store: item.store,
          category: item.category || null,
          price: item.salePrice,
        }),
      });
    } catch {}
  }

  function logout() {
    localStorage.removeItem('smartcart-token');
    setUser(null);
  }

  function loginWithGoogle() {
    window.location.href = `${API}/auth/google`;
  }

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, updateProfile, recordBehaviour, refreshUser, toggleFavourite, loginWithGoogle, getToken }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
